import { Component } from "react";

import { PlayerStatsWrapper } from "../../types/stats/player-stats";
import NumberSelector from "../entity-management/entity-selectors/number-selector";
import { NumberComparison } from "../stats-editing-components/number-comparison";
import {
  areStatsEqual,
  getToolTipMessage,
} from "../stats-editing-components/stats-editing-utils";
import StealStatsButton from "../stats-editing-components/steal-stats-button";
import StealStatsHeadingAndButton from "../stats-editing-components/steal-stats-heading-and-button";

import { batchUpdate } from "./squad-page-utils";

interface Props {
  index: number;
  maxBallNumber: number;
  minBallNumber: number;
  title: string;
  currentStats: PlayerStatsWrapper;
  comparedStats: PlayerStatsWrapper;
  comparedUserName: string;
  classes: string;
  onArrayElementValid: (property: string, index: number, value) => void;
  onUpdate: (updatedStats: PlayerStatsWrapper) => void;
}

export class BattingStatByBallDisplay extends Component<Props> {
  STATS_TO_STEAL: string[] = [
    "upToBalls",
    "upToBallsStrikeRateBias",
    "upToBallsWicketBias",
    "upToBallsConfidence",
  ];

  onClickHandler = () => {
    batchUpdate(
      this.props.currentStats,
      this.props.comparedStats,
      this.STATS_TO_STEAL,
      this.props.onUpdate,
      this.props.index
    );
  };

  public render() {
    return (
      <div className="stats-modal-section">
        <div
          className={
            this.props.classes + " stats-modal-section-head-with-inline-spinner"
          }
        >
          {this.showUpToBallsSpinner() && (
            <NumberSelector
              label="Up to ball..."
              min={this.props.minBallNumber}
              max={this.props.maxBallNumber}
              step={1}
              initial={
                this.props.currentStats.playerStats.upToBalls[this.props.index]
              }
              onValid={(newValue) =>
                this.props.onArrayElementValid(
                  "upToBalls",
                  this.props.index,
                  newValue
                )
              }
              style={{ padding: "0px" }}
              textFieldStyle={{
                height: "18px",
                width: "50px",
              }}
              buttonStyle={{
                maxHeight: "8.5px",
                minHeight: "8.5px",
              }}
            />
          )}
          {!this.showUpToBallsSpinner() &&
            "After Ball " + this.props.minBallNumber}
        </div>
        <div>
          <NumberSelector
            label="Strike Rate"
            min={0}
            max={100000000}
            step={0.01}
            decimalPlaces={2}
            initial={
              this.props.currentStats.playerStats.upToBallsStrikeRateBias[
                this.props.index
              ]
            }
            onValid={(newValue) =>
              this.props.onArrayElementValid(
                "upToBallsStrikeRateBias",
                this.props.index,
                newValue
              )
            }
          />
          <NumberSelector
            label="Wicket Percent"
            min={0}
            max={100000000}
            step={0.01}
            decimalPlaces={2}
            initial={
              this.props.currentStats.playerStats.upToBallsWicketBias[
                this.props.index
              ]
            }
            onValid={(newValue) =>
              this.props.onArrayElementValid(
                "upToBallsWicketBias",
                this.props.index,
                newValue
              )
            }
          />
          <NumberSelector
            label="Confidence"
            min={0}
            max={100000000}
            step={1}
            initial={
              this.props.currentStats.playerStats.upToBallsConfidence[
                this.props.index
              ]
            }
            onValid={(newValue) =>
              this.props.onArrayElementValid(
                "upToBallsConfidence",
                this.props.index,
                newValue
              )
            }
          />
          {!!this.props.comparedStats && (
            <div className="comparison">
              <StealStatsHeadingAndButton
                comparedUserName={this.props.comparedUserName}
              >
                <StealStatsButton
                  comparedUserName={this.props.comparedUserName}
                  disabled={areStatsEqual(
                    this.props.currentStats,
                    this.props.comparedStats,
                    this.STATS_TO_STEAL,
                    "playerStats",
                    this.props.index
                  )}
                  tooltipMessage={getToolTipMessage(
                    this.props.currentStats,
                    this.props.comparedStats,
                    this.STATS_TO_STEAL,
                    this.props.comparedUserName,
                    "playerStats",
                    this.props.index
                  )}
                  onClickHandler={this.onClickHandler}
                />
              </StealStatsHeadingAndButton>
              {this.showUpToBallsSpinner() && (
                <NumberComparison
                  label="Up to ball"
                  originalValue={
                    this.props.currentStats.playerStats.upToBalls[
                      this.props.index
                    ]
                  }
                  comparedValue={
                    this.props.comparedStats.playerStats.upToBalls[
                      this.props.index
                    ]
                  }
                />
              )}
              {!this.showUpToBallsSpinner() && (
                <NumberComparison
                  label="After ball"
                  originalValue={
                    this.props.currentStats.playerStats.upToBalls[
                      this.props.index - 1
                    ]
                  }
                  comparedValue={
                    this.props.comparedStats.playerStats.upToBalls[
                      this.props.index - 1
                    ]
                  }
                />
              )}
              <NumberComparison
                label="Strike Rate"
                originalValue={
                  this.props.currentStats.playerStats.upToBallsStrikeRateBias[
                    this.props.index
                  ]
                }
                comparedValue={
                  this.props.comparedStats.playerStats.upToBallsStrikeRateBias[
                    this.props.index
                  ]
                }
              />
              <NumberComparison
                label="Wicket Percent"
                originalValue={
                  this.props.currentStats.playerStats.upToBallsWicketBias[
                    this.props.index
                  ]
                }
                comparedValue={
                  this.props.comparedStats.playerStats.upToBallsWicketBias[
                    this.props.index
                  ]
                }
              />
              <NumberComparison
                label="Confidence"
                originalValue={
                  this.props.currentStats.playerStats.upToBallsConfidence[
                    this.props.index
                  ]
                }
                comparedValue={
                  this.props.comparedStats.playerStats.upToBallsConfidence[
                    this.props.index
                  ]
                }
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  private showUpToBallsSpinner(): boolean {
    return (
      !!this.props.currentStats &&
      !!this.props.currentStats.playerStats &&
      this.props.currentStats.playerStats.upToBalls.length > this.props.index
    );
  }
}
